import { useEffect, useState } from "react";
import { Tag, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const DOWS = [
  [0, "Δευτέρα"], [1, "Τρίτη"], [2, "Τετάρτη"], [3, "Πέμπτη"], [4, "Παρασκευή"], [5, "Σάββατο"], [6, "Κυριακή"],
];

export const OffersAdmin = () => {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [days, setDays] = useState([]);
  const [enabled, setEnabled] = useState(true);

  const reload = () => api.get("/admin/offer-schedule").then(({ data: d }) => {
    setData(d);
    setDays(d.schedule.days || []);
    setEnabled(d.schedule.enabled !== false);
  }).catch(() => setData({ schedule: { days: [] }, categories: [] }));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/offer-schedule", { enabled, days });
      toast.success(lang === "en" ? "Saved" : "Αποθηκεύτηκε");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const update = (i, patch) => setDays(days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const toggleCat = (i, cat) => {
    const cats = days[i].categories || ["all"];
    const next = cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats.filter((c) => c !== "all"), cat];
    update(i, { categories: next.length ? next : ["all"] });
  };

  if (!data) return <p className="text-[#64748B] mt-10">…</p>;

  return (
    <div className="mt-10" data-testid="admin-offers-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-heading font-black text-2xl text-[#0B1B3A] flex items-center gap-2">
          <Tag className="w-5 h-5 text-[#1D4ED8]" /> {lang === "en" ? "Live offer days" : "Ημέρες προσφορών (live)"}
        </h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-bold text-[#0B1B3A]">
            <input data-testid="offers-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {lang === "en" ? "Offers enabled" : "Προσφορές ενεργές"}
          </label>
          <button data-testid="offers-save" onClick={save} className="btn-blue !px-5 !py-2 text-xs inline-flex items-center gap-2"><Save className="w-3.5 h-3.5" /> {lang === "en" ? "Save" : "Αποθήκευση"}</button>
        </div>
      </div>

      <p className="text-sm text-[#475569] mt-3 max-w-3xl">
        {lang === "en"
          ? "Each offer day discounts the selected categories live inside the course catalog. A daily job tunes the discount between min and max based on the last 7 days of sales."
          : "Κάθε ημέρα προσφοράς εφαρμόζει έκπτωση live μέσα στον κατάλογο μαθημάτων. Ένα ημερήσιο job ρυθμίζει το ποσοστό μεταξύ min/max ανάλογα με τις πωλήσεις της τελευταίας εβδομάδας."}
      </p>
      {data.today?.active && (
        <p className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#0B1B3A] bg-[#EEF4FF] border border-[#BFDBFE] rounded-full px-4 py-2" data-testid="offers-today">
          {lang === "en" ? "Today" : "Σήμερα"}: {data.today.label} · −{data.today.discount}%
        </p>
      )}

      <div className="space-y-4 mt-6" data-testid="offers-list">
        {days.map((d, i) => (
          <div key={i} className="card-dark p-5" data-testid={`offer-day-${i}`}>
            <div className="flex flex-wrap items-end gap-3">
              <select data-testid={`offer-dow-${i}`} value={d.dow} onChange={(e) => update(i, { dow: Number(e.target.value) })} className="input-cream w-36">
                {DOWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input data-testid={`offer-label-${i}`} value={d.label || ""} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label EL" className="input-cream w-48" />
              <input data-testid={`offer-label-en-${i}`} value={d.label_en || ""} onChange={(e) => update(i, { label_en: e.target.value })} placeholder="Label EN" className="input-cream w-48" />
              <input data-testid={`offer-discount-${i}`} type="number" min={0} max={90} value={d.discount ?? 0} onChange={(e) => update(i, { discount: Number(e.target.value) })} placeholder="%" className="input-cream w-20" />
              <input data-testid={`offer-min-${i}`} type="number" min={0} max={90} value={d.min ?? 10} onChange={(e) => update(i, { min: Number(e.target.value) })} placeholder="min%" className="input-cream w-20" />
              <input data-testid={`offer-max-${i}`} type="number" min={0} max={90} value={d.max ?? 40} onChange={(e) => update(i, { max: Number(e.target.value) })} placeholder="max%" className="input-cream w-20" />
              <button data-testid={`offer-delete-${i}`} onClick={() => setDays(days.filter((_, idx) => idx !== i))} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {["all", ...(data.categories || [])].map((cat) => {
                const on = (d.categories || ["all"]).includes(cat);
                return (
                  <button key={cat} data-testid={`offer-cat-${i}-${cat}`} onClick={() => (cat === "all" ? update(i, { categories: ["all"] }) : toggleCat(i, cat))}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors duration-200 ${on ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-transparent text-[#334155] border-[#E2E8F0]"}`}>
                    {cat === "all" ? (lang === "en" ? "All courses" : "Όλα τα μαθήματα") : cat}
                  </button>
                );
              })}
            </div>
            {d.weekly_sales !== undefined && (
              <p className="text-xs text-[#64748B] mt-3">{lang === "en" ? "Sales last 7 days" : "Πωλήσεις 7 ημερών"}: {d.weekly_sales}</p>
            )}
          </div>
        ))}
      </div>

      <button data-testid="offers-add" onClick={() => setDays([...days, { dow: 1, label: "", label_en: "", discount: 20, min: 10, max: 40, categories: ["all"] }])}
        className="btn-outline inline-flex items-center gap-2 mt-5"><Plus className="w-4 h-4" /> {lang === "en" ? "Add offer day" : "Νέα ημέρα προσφοράς"}</button>
    </div>
  );
};

export default OffersAdmin;
